import { Card, Input, Select, Space, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ErrorAlert } from '@/components/ErrorAlert';
import { formatDateTime } from '@/lib/format';
import { ChannelTag } from '@/messages/ChannelTag';
import { MessageStatusTag } from '@/messages/MessageStatusTag';
import type { MessageStatus } from '@/messages/hooks';
import {
  useOtpJournalQuery,
  useSmsJournalQuery,
  type JournalEntry,
  type OtpJournalEntry,
} from './hooks';

const { Title, Text } = Typography;

// Статусы для фильтра. Кросс-tenant журнал показывает и Telegram, и SMS —
// поэтому даём полный набор терминальных статусов, а не только delivered/skipped
// из прототипа. Метки — общие с тенантным журналом (`messages.status.*`).
const STATUS_OPTIONS: MessageStatus[] = ['sent', 'delivered', 'skipped', 'failed'];

type JournalTab = 'messages' | 'otp';

const OTP_FLOWS = ['login', 'org_verification', 'org_contact'] as const;
const OTP_STATUSES = ['sent', 'failed', 'skipped'] as const;

// Тег исхода OTP-SMS: sent — зелёный, failed — красный, skipped — серый.
function OtpStatusTag({ status }: { status: string }): JSX.Element {
  const { t } = useTranslation();
  const color = status === 'sent' ? 'green' : status === 'failed' ? 'red' : undefined;
  return <Tag color={color}>{t(`platform.journal.otp.status.${status}`, status)}</Tag>;
}

// Кросс-tenant журнал сообщений (M5.4). Переиспользует презентацию тенантного
// журнала: ChannelTag + MessageStatusTag (те же теги «Доставлено»/«Пропущено»).
// Отличие — кросс-tenant: добавлены колонки «Оптика» и «Клиент», данные из
// /platform/sms-journal.
// №20: вторая вкладка «Коды (OTP)» — OTP-SMS вне журнала `message`
// (вход по телефону, верификация организации, контакты) из /platform/otp-journal:
// раньше судьба кода была видна только в логе воркера.
export function PlatformJournalPage(): JSX.Element {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab: JournalTab = searchParams.get('tab') === 'otp' ? 'otp' : 'messages';
  const page = Number(searchParams.get('page') ?? '1') || 1;
  const pageSize = Number(searchParams.get('page_size') ?? '20') || 20;
  const channel = searchParams.get('channel') ?? undefined;
  const status = searchParams.get('status') ?? undefined;
  const flow = searchParams.get('flow') ?? undefined;
  const phone = searchParams.get('phone') ?? undefined;

  const messages = useSmsJournalQuery(
    { page, pageSize, channel, status },
    { enabled: tab === 'messages' },
  );
  const otp = useOtpJournalQuery(
    { page, pageSize, flow, status, phone },
    { enabled: tab === 'otp' },
  );
  const { data, isLoading, error } = tab === 'otp' ? otp : messages;

  const setParam = (name: string, value: string | undefined): void => {
    const sp = new URLSearchParams(searchParams);
    if (value === undefined || value === '') sp.delete(name);
    else sp.set(name, value);
    sp.set('page', '1');
    setSearchParams(sp);
  };

  const columns: ColumnsType<JournalEntry> = [
    {
      title: t('platform.journal.time'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v: string) => (
        <Text type="secondary" className="sl-mono" style={{ fontSize: 12 }}>
          {formatDateTime(v)}
        </Text>
      ),
    },
    {
      title: t('platform.journal.optic'),
      dataIndex: 'optic_name',
      key: 'optic_name',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: t('platform.journal.client'),
      dataIndex: 'client_name',
      key: 'client_name',
      render: (v: string) => <Text type="secondary">{v}</Text>,
    },
    {
      title: t('platform.journal.channel'),
      dataIndex: 'channel',
      key: 'channel',
      width: 120,
      render: (c: string) => <ChannelTag channel={c} />,
    },
    {
      title: t('platform.journal.status'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (st: string) => <MessageStatusTag status={st as MessageStatus} />,
    },
  ];

  const otpColumns: ColumnsType<OtpJournalEntry> = [
    {
      title: t('platform.journal.time'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v: string) => (
        <Text type="secondary" className="sl-mono" style={{ fontSize: 12 }}>
          {formatDateTime(v)}
        </Text>
      ),
    },
    {
      title: t('platform.journal.otp.phone'),
      dataIndex: 'phone',
      key: 'phone',
      width: 170,
      render: (v: string) => (
        <Text strong className="sl-mono">
          {v}
        </Text>
      ),
    },
    {
      title: t('platform.journal.otp.flow'),
      dataIndex: 'flow',
      key: 'flow',
      render: (v: string) => <Text>{t(`platform.journal.otp.flows.${v}`, v)}</Text>,
    },
    {
      title: t('platform.journal.status'),
      key: 'status',
      width: 260,
      render: (_v, r) => (
        <Space size={6}>
          <OtpStatusTag status={r.status} />
          {r.error_code && (
            <Text type="secondary" className="sl-mono" style={{ fontSize: 12 }}>
              {r.error_code}
            </Text>
          )}
        </Space>
      ),
    },
  ];

  const total = data?.total ?? 0;

  const onTabChange = (key: string): void => {
    const sp = new URLSearchParams();
    if (key === 'otp') sp.set('tab', 'otp');
    setSearchParams(sp);
  };

  const pagination = {
    current: page,
    pageSize,
    total,
    showSizeChanger: false,
    onChange: (p: number, ps: number) => {
      const sp = new URLSearchParams(searchParams);
      sp.set('page', String(p));
      sp.set('page_size', String(ps));
      setSearchParams(sp);
    },
  };

  return (
    <div>
      <Title level={3} style={{ marginBottom: 4 }}>
        {t('platform.journal.title')}
      </Title>
      <Text type="secondary">{t('platform.journal.subtitle')}</Text>

      <Card style={{ marginTop: 16, marginBottom: 16 }} variant="outlined">
        <Tabs
          activeKey={tab}
          onChange={onTabChange}
          items={[
            { key: 'messages', label: t('platform.journal.tabs.messages') },
            { key: 'otp', label: t('platform.journal.tabs.otp') },
          ]}
        />
        {tab === 'messages' ? (
          <Space size="middle" wrap>
            <Select<string | undefined>
              allowClear
              placeholder={t('platform.journal.channel_all')}
              value={channel}
              onChange={(v) => setParam('channel', v)}
              style={{ minWidth: 180 }}
              options={[
                { value: 'telegram', label: 'Telegram' },
                { value: 'sms', label: 'SMS' },
              ]}
            />
            <Select<string | undefined>
              allowClear
              placeholder={t('platform.journal.status_all')}
              value={status}
              onChange={(v) => setParam('status', v)}
              style={{ minWidth: 180 }}
              options={STATUS_OPTIONS.map((s) => ({
                value: s,
                label: t(`messages.status.${s}`),
              }))}
            />
          </Space>
        ) : (
          <Space size="middle" wrap>
            <Select<string | undefined>
              allowClear
              placeholder={t('platform.journal.otp.flow_all')}
              value={flow}
              onChange={(v) => setParam('flow', v)}
              style={{ minWidth: 220 }}
              options={OTP_FLOWS.map((f) => ({
                value: f,
                label: t(`platform.journal.otp.flows.${f}`),
              }))}
            />
            <Select<string | undefined>
              allowClear
              placeholder={t('platform.journal.status_all')}
              value={status}
              onChange={(v) => setParam('status', v)}
              style={{ minWidth: 180 }}
              options={OTP_STATUSES.map((s) => ({
                value: s,
                label: t(`platform.journal.otp.status.${s}`),
              }))}
            />
            <Input.Search
              allowClear
              placeholder={t('platform.journal.otp.phone_search')}
              defaultValue={phone}
              onSearch={(v) => setParam('phone', v.trim() || undefined)}
              style={{ width: 220 }}
            />
          </Space>
        )}
      </Card>

      {error && <ErrorAlert error={error} />}

      {tab === 'messages' ? (
        <Table<JournalEntry>
          rowKey="id"
          loading={isLoading}
          dataSource={(data?.items ?? []) as JournalEntry[]}
          columns={columns}
          pagination={pagination}
          locale={{ emptyText: t('platform.journal.empty') }}
        />
      ) : (
        <Table<OtpJournalEntry>
          rowKey="id"
          loading={isLoading}
          dataSource={(data?.items ?? []) as OtpJournalEntry[]}
          columns={otpColumns}
          pagination={pagination}
          locale={{ emptyText: t('platform.journal.otp.empty') }}
        />
      )}

      <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
        {tab === 'messages'
          ? t('platform.journal.footer_note')
          : t('platform.journal.otp.footer_note')}
      </Text>
    </div>
  );
}
